import { Component } from '@theme/component';
import { parseRaceDuration, formatRaceDuration } from './brox-race-time.js';

/**
 * Arithmetic planning only: eight kilometre runs plus station and transition minutes.
 * @extends {Component<{fields: HTMLFieldSetElement, pace: HTMLInputElement,
 * stations: HTMLInputElement, transitions: HTMLInputElement, minutes: HTMLElement,
 * seconds: HTMLElement, units: HTMLElement, breakdown: HTMLElement,
 * paceError: HTMLElement, stationError: HTMLElement, transitionError: HTMLElement,
 * loading: HTMLElement, runTotal?: HTMLElement, stationTotal?: HTMLElement, transitionTotal?: HTMLElement}>}
 */
class BroxRaceCalculator extends Component {
  requiredRefs = ['fields', 'pace', 'stations', 'transitions', 'minutes', 'seconds',
    'units', 'breakdown', 'paceError', 'stationError', 'transitionError', 'loading'];

  connectedCallback() { super.connectedCallback(); this.#initialize(); }
  updatedCallback() { super.updatedCallback(); this.#initialize(); }
  disconnectedCallback() { super.disconnectedCallback(); }

  #initialize() {
    this.refs.fields.disabled = false;
    this.refs.loading.hidden = true;
    this.#calculate();
  }

  /** Declarative input/change handler; no network, storage or application side effects.
   * @param {Event} event
   */
  handleUpdate(event) {
    if (!(event.target instanceof HTMLInputElement) || !this.contains(event.target)) return;
    this.#calculate();
  }

  /** Recalculate the entered duration totals without account or network access. */
  handleCalculate() { this.#calculate(); }

  #calculateDurations() {
    const rows = [
      [this.refs.pace, this.refs.paceError, this.refs.runTotal],
      [this.refs.stations, this.refs.stationError, this.refs.stationTotal],
      [this.refs.transitions, this.refs.transitionError, this.refs.transitionTotal],
    ];
    let valid = true;
    let total = 0;
    for (const [input, error, output] of rows) {
      if (!(input instanceof HTMLInputElement) || !(error instanceof HTMLElement)) continue;
      const seconds = parseRaceDuration(input.value);
      const inputValid = seconds !== null;
      error.hidden = inputValid;
      if (inputValid) input.removeAttribute('aria-invalid');
      else input.setAttribute('aria-invalid', 'true');
      if (output instanceof HTMLElement) output.textContent = inputValid ? formatRaceDuration(seconds) : '—';
      if (inputValid) total += seconds;
      else valid = false;
    }
    this.refs.units.hidden = true;
    this.refs.minutes.textContent = valid ? formatRaceDuration(total) : '—';
    this.refs.breakdown.textContent = valid ? '' : (this.dataset.durationError ?? '');
  }

  #calculate() {
    if (this.dataset.durationMode === 'true') { this.#calculateDurations(); return; }
    const { pace, stations, transitions } = this.refs;
    const match = /^(\d{1,2}):([0-5]\d)$/.exec(pace.value.trim());
    const paceSeconds = match ? Number(match[1]) * 60 + Number(match[2]) : NaN;
    const paceValid = Number.isFinite(paceSeconds) && paceSeconds >= 180 && paceSeconds <= 900;
    const stationsValid = stations.value !== '' && stations.validity.valid;
    const transitionsValid = transitions.value !== '' && transitions.validity.valid;

    for (const [input, error, valid] of [
      [pace, this.refs.paceError, paceValid],
      [stations, this.refs.stationError, stationsValid],
      [transitions, this.refs.transitionError, transitionsValid],
    ]) {
      if (!(input instanceof HTMLInputElement) || !(error instanceof HTMLElement)) continue;
      error.hidden = Boolean(valid);
      if (valid) input.removeAttribute('aria-invalid');
      else input.setAttribute('aria-invalid', 'true');
    }

    const valid = paceValid && stationsValid && transitionsValid;
    this.refs.units.hidden = !valid;
    if (!valid) {
      this.refs.minutes.textContent = '—';
      this.refs.breakdown.textContent = this.dataset.invalid ?? '';
      return;
    }
    const runSeconds = paceSeconds * 8;
    const totalSeconds = Math.round(runSeconds + (stations.valueAsNumber + transitions.valueAsNumber) * 60);
    this.refs.minutes.textContent = String(Math.floor(totalSeconds / 60));
    this.refs.seconds.textContent = String(totalSeconds % 60);
    this.refs.breakdown.textContent = (this.dataset.breakdown ?? '')
      .replace('[running]', (runSeconds / 60).toFixed(1))
      .replace('[stations]', String(stations.valueAsNumber))
      .replace('[transitions]', String(transitions.valueAsNumber));
  }
}

if (!customElements.get('brox-race-calculator')) {
  customElements.define('brox-race-calculator', BroxRaceCalculator);
}
